// The transport half of every presigned video upload: push bytes to S3, call the
// API, retry what is worth retrying. Shared by the exercise-library and workflow
// upload helpers, which differ only in which routes they call.

/** Direct multipart POST of the file to S3 using the presigned policy fields. */
export async function postToS3({ url, fields }, file) {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  form.append("Content-Type", file.type);
  form.append("file", file); // the file part must be appended last for S3
  const res = await fetch(url, { method: "POST", body: form });
  if (!res.ok) {
    throw new Error(`Direct video upload to storage failed (${res.status})`);
  }
}

export async function postJson(api, path, body) {
  const res = await api(path, { method: "POST", body });
  if (!res.ok) {
    const error = new Error(`Video upload request failed (${res.status})`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

// A 4xx fails the same way next time; only a busy gateway or a dropped connection
// (no status at all) is worth another attempt.
const RETRYABLE_STATUSES = [408, 425, 429, 502, 503, 504];
const RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** `request` must be idempotent — it may run several times. */
export async function withRetry(request, label) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await request();
    } catch (error) {
      const retryable =
        error.status === undefined || RETRYABLE_STATUSES.includes(error.status);
      if (attempt >= RETRY_ATTEMPTS || !retryable) throw error;
      const delay = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
      console.warn(
        `[video-upload] ${label} failed, retry ${attempt} in ${delay}ms`,
        error.message
      );
      await wait(delay);
    }
  }
}
